import { useEffect, useRef } from "react";
import { useReducedMotion } from "@mantine/hooks";
import { resolvePublic } from "@/lib/media";

/**
 * Hero backdrop. Fills its (positioned) parent absolutely and adds a fade-down
 * gradient at the bottom so it blends into the page background — the parent
 * just needs `position: relative` and a reasonable height.
 *
 * Reduced-motion users get a static poster image instead of the looping video.
 */
export function HeroBackdrop() {
  const reduced = useReducedMotion();
  const poster = resolvePublic("public/artwork/site/hero/hero-static-poster.jpg");
  const videoRef = useRef<HTMLVideoElement>(null);

  // Browser extensions that mess with video aspect ratios (e.g. ultrawidify)
  // inject classes + inline styles that constrain the video. We fight back
  // three ways: (1) inline !important on width/height/transform; (2) actively
  // strip injected classes that match known extension prefixes; (3) re-apply
  // on a MutationObserver in case the extension re-stomps after our changes.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const EXT_CLASS_RE = /^(uw-|ultrawidify)/;
    const force = () => {
      el.style.setProperty("width", "100%", "important");
      el.style.setProperty("height", "100%", "important");
      el.style.setProperty("object-fit", "cover", "important");
      el.style.setProperty("transform", "none", "important");
      el.style.setProperty("max-width", "none", "important");
      // Remove ultrawidify-style class injections — they apply width/transform
      // that we don't want.
      for (const cls of Array.from(el.classList)) {
        if (EXT_CLASS_RE.test(cls)) el.classList.remove(cls);
      }
    };
    force();
    const obs = new MutationObserver(force);
    obs.observe(el, { attributes: true, attributeFilter: ["style", "class"] });
    return () => obs.disconnect();
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        background: "var(--color-deeper)",
      }}
    >
      {reduced ? (
        <img
          src={poster}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 1,
          }}
        />
      ) : (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          poster={poster}
          preload="metadata"
          className="hero-backdrop-video"
          style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 1 }}
        >
          <source
            src={resolvePublic("public/media/site/hero/bg-loop-1280.webm")}
            type="video/webm"
            media="(min-width: 768px)"
          />
          <source
            src={resolvePublic("public/media/site/hero/bg-loop-1280.mp4")}
            type="video/mp4"
            media="(min-width: 768px)"
          />
          <source
            src={resolvePublic("public/media/site/hero/bg-loop-960.webm")}
            type="video/webm"
          />
          <source
            src={resolvePublic("public/media/site/hero/bg-loop-960.mp4")}
            type="video/mp4"
          />
        </video>
      )}

      {/* Top tint to keep header readable + bottom fade-out that blends the
          hero into the page background below. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(13,14,31,0.55) 0%, rgba(13,14,31,0.15) 18%, rgba(14,11,12,0) 35%, rgba(14,11,12,0) 60%, rgba(14,11,12,0.85) 88%, rgba(14,11,12,1) 100%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
